'use client';

import { ReactNode, RefObject, use } from 'react';
import { FormProvider } from 'react-hook-form';

import styled from '@emotion/styled';
import { cn } from 'cn';

import { ActivityStore } from '@/shared-survey';
import { Chat, type ChatClasses, type ChatIntroVideo } from '@/shared-ui';
import { SurveyThemeContext } from '@/shared-utilities';
import { ProjectType } from '@/survey-graphql';
import { ScrollArea } from '@/ui';

import { getTypeOfRenderer } from './project-response-chat.helpers';
import { useProjectChat } from './project-response-chat.hooks';
import { hasBrandSeparation } from './project-response-chat.types';
import ProjectMessageForm from './project-response-form';
import { POLL_MESSAGE_LOADING_UUID } from './project-response.helpers';

const StyledProjectChatRoot = styled.div<{
  themeStyles?: Record<string, string>;
}>`
  ${(props) => props.themeStyles};
`;

type ProjectMessageFormProps = React.ComponentProps<typeof ProjectMessageForm>;

type Props = Omit<ProjectMessageFormProps, 'onSubmit'> & {
  messages: React.ComponentProps<typeof Chat>['messages'];
  classes?: ChatClasses;
  avatarUrl?: string | null;
  autoScrollToLatest?: boolean;
  insightLoading?: boolean;
  isConversationOver?: boolean;
  onConversationOver?: () => void;
  hideNewLineText?: boolean;
  renderMobileView?: boolean;
  introText?: string | null;
  introVideo?: ChatIntroVideo | null;
  activityStore?: RefObject<ActivityStore>;
  isInsightShare?: boolean;
  isAiVoiceEnabled?: boolean;
  isUserVoiceEnabled?: boolean;
  projectType?: ProjectType;
  numeralLocale?: string | null;
  /** True for a typeless (V2-authored) project, whose response is recorded as one chat session per primary question. */
  hasQuestionSessions?: boolean;
  renderMediaOptionsInline?: boolean;
  onReportProblemSubmit?: (
    description: string,
    context: { messageId?: string; questionId?: string }
  ) => void | Promise<void>;
  /** Optional extra nodes rendered inside the FormProvider tree (so they can access useFormContext). */
  formProviderChildren?: ReactNode;
  /** Optional node rendered inside the chat card, beneath the messages/form (same section as the conversation). */
  footerSlot?: ReactNode;
} & (
    | {
        readonly: true;
        onSubmit?: never;
      }
    | {
        readonly?: false;
        onSubmit: ProjectMessageFormProps['onSubmit'];
      }
  );

export default function ProjectChat({
  disabled,
  loading,
  messages,
  onSubmit,
  classes,
  avatarUrl,
  autoScrollToLatest = true,
  insightLoading,
  isConversationOver,
  hideNewLineText = true,
  onConversationOver,
  readonly,
  renderMobileView,
  introText,
  introVideo,
  conversationOverText,
  activityStore,
  isInsightShare = false,
  isAiVoiceEnabled = false,
  isUserVoiceEnabled = false,
  projectType,
  numeralLocale,
  hasQuestionSessions = false,
  renderMediaOptionsInline,
  onReportProblemSubmit,
  formProviderChildren,
  footerSlot,
}: Props) {
  const { styles } = use(SurveyThemeContext);

  const {
    renderAssistantMessage,
    formatUserMessage,
    formValue,
    lastAssistantMessage,
    disableForm,
  } = useProjectChat({
    disabled,
    messages,
    activityStore,
    readonly,
    isInsightShare,
    isAiVoiceEnabled,
    onReportProblemSubmit,
    projectType,
    numeralLocale,
    hasQuestionSessions,
    renderMediaOptionsInline,
  });

  return (
    <FormProvider {...formValue}>
      <StyledProjectChatRoot
        data-test='project-chat-root'
        themeStyles={styles}
        className={cn(
          'flex w-full flex-1 flex-col items-center rounded-4xl bg-(image:--brand-bg-image) bg-cover bg-center bg-no-repeat p-4',
          classes?.root
        )}
      >
        <div
          className={cn('relative min-h-0 w-full flex-1', classes?.container)}
        >
          <ScrollArea className='absolute inset-0'>
            <Chat
              messages={messages}
              loading={insightLoading}
              renderAssistantMessage={renderAssistantMessage}
              formatUserMessage={formatUserMessage}
              avatarUrl={avatarUrl}
              autoScrollToLatest={autoScrollToLatest}
              scrollToBottomDep={messages
                .map((e) => e.id)
                .filter((e) => e !== POLL_MESSAGE_LOADING_UUID)
                .join(',')}
              classes={{
                message: classes?.message,
                welcomeWrapper: classes?.welcomeWrapper,
                welcomeContainer: classes?.welcomeContainer,
                welcomeContent: classes?.welcomeContent,
                welcomeSeparator: classes?.welcomeSeparator,
                welcomeText: classes?.welcomeText,
                welcomeVideo: classes?.welcomeVideo,
              }}
              introText={introText}
              introVideo={introVideo}
              renderMobileView={renderMobileView}
              hasBrandSeparation={hasBrandSeparation(styles)}
              themed
            />
          </ScrollArea>
        </div>
        {!readonly ? (
          <div
            data-test='project-chat-form-container'
            className={cn('w-full shrink-0', classes?.formGradientOuter)}
          >
            <ProjectMessageForm
              questionType={
                lastAssistantMessage
                  ? getTypeOfRenderer(lastAssistantMessage, projectType)
                  : undefined
              }
              loading={loading}
              disabled={disableForm}
              onSubmit={onSubmit}
              isConversationOver={isConversationOver}
              hideNewLineText={hideNewLineText}
              onConversationOver={onConversationOver}
              renderMobileView={renderMobileView}
              conversationOverText={conversationOverText}
              isUserVoiceEnabled={isUserVoiceEnabled}
              formGradientOuter={classes?.formGradientOuter}
              formGradientInner={classes?.formGradientInner}
            />
          </div>
        ) : null}
        {footerSlot ? (
          <div className='w-full shrink-0'>{footerSlot}</div>
        ) : null}
      </StyledProjectChatRoot>
      {formProviderChildren}
    </FormProvider>
  );
}
